// application_activity(pin):false
// campaign_activities(pin):false
// mentions(pin):false
// project_activities(pin):false
// project_recommendation(pin):false
// shared_content(pin):false

import { ITutorialState } from '../Tutorial/Tutorial';

// email(pin):false
// phone(pin):false
// sms(pin):false

interface ISettingsData {
    header: string;
    body: string;
    service_name: string;
    parent_name: string;
    /** MUI icon key for account settings toggle rows (appConfig only). */
    toggleIcon?: 'explore' | 'tv_gen' | 'assignment_ind';
}

interface ICookieData {
    name: string;
    description: string;
    title: string;
}

export const settings: ISettingsData[] = [
    {
        header: 'Subscriptions',
        body: "Notify me about activity from the pages I'm watching.",
        service_name: 'application_activity',
        parent_name: 'web_notifications_settings',
    },
    {
        header: 'Recommended projects',
        body: 'Notify me of projects that I might like based on what I watch.',
        service_name: 'project_recommendation',
        parent_name: 'web_notifications_settings',
    },
    {
        header: 'Campaign Recommendations',
        body: 'Notify me about campaigns I might be interested in.',
        service_name: 'campaign_activities',
        parent_name: 'web_notifications_settings',
    },
    {
        header: 'Mentions',
        body: 'Notify me when others mention my name.',
        service_name: 'mentions',
        parent_name: 'web_notifications_settings',
    },
    {
        header: 'Shared content',
        body: 'Notify me when others share my projects.',
        service_name: 'shared_content',
        parent_name: 'web_notifications_settings',
    },
    {
        header: 'Vote Summary',
        body: 'Notify me with daily or weekly updates on my vote counts.',
        service_name: 'vote_activity',
        parent_name: 'web_notifications_settings',
    },
    {
        header: 'Jury Activity',
        body: 'Notify me with new jury activities.',
        service_name: 'jury_activity',
        parent_name: 'web_notifications_settings',
    },
];

export const communication: ISettingsData[] = [
    {
        header: 'Phone',
        body: 'Notify me via Phone when available.',
        service_name: 'phone',
        parent_name: 'web_communication_preference',
    },
    {
        header: 'SMS',
        body: 'Notify me via short message service (SMS).',
        service_name: 'sms',
        parent_name: 'web_communication_preference',
    },
    {
        header: 'Email',
        body: 'Notify me of important events via email.',
        service_name: 'email',
        parent_name: 'web_communication_preference',
    },
];
export const cookiePreference: ICookieData[] = [
    {
        name: 'essential',
        title: 'Essential (Always On)',
        description:
            'These cookies are the backbone of our website, ensuring it functions smoothly and securely. Think of them as the invisible helpers making everything run behind the scenes.',
    },
    {
        name: 'performace',
        title: 'Performance',
        description:
            'Want to help us make the website even better? These cookies track how you use the site, revealing areas for improvement and ultimately creating a smoother and more enjoyable experience for everyone.',
    },
    {
        name: 'functionality',
        title: 'Functionality',
        description:
            'Remember your preferences, keep you logged in, and personalize your journey. These cookies are your friendly website assistants, saving you time and remembering your choices for a tailored experience.',
    },
    {
        name: 'targeting',
        title: 'Targeting/Advertising',
        description:
            "Like seeing relevant ads and offers? These cookies understand your interests and show you content that might pique your curiosity, ensuring you don't miss out on things you might love.",
    },
];
export const appConfig: ISettingsData[] = [
    {
        header: 'Guided Tour',
        body: 'Enable guided tour to help you navigate through the platform.',
        service_name: 'show_app_guide',
        parent_name: 'appConfig',
        toggleIcon: 'explore',
    },
    {
        header: 'Introduction',
        body: 'Show introduction screen when entering a new production space.',
        service_name: 'show_introduction',
        parent_name: 'appConfig',
        toggleIcon: 'tv_gen',
    },
    {
        header: 'Project Guide',
        body: 'Activate contextual project assistance for casting directors.',
        service_name: 'show_project_guide',
        parent_name: 'appConfig',
        toggleIcon: 'assignment_ind',
    },
];

export const participantWorkspaceTutorialSteps: ITutorialState = {
    run: true,
    steps: [
        {
            content: "View the creator's profile, social stats, and engagement rate. Use Mark Complete to release their payment once everything is done.",
            disableBeacon: true,
            disableOverlayClose: true,
            hideCloseButton: false,
            hideFooter: false,
            placement: 'bottom',
            spotlightClicks: false,
            styles: { options: { zIndex: 9998 } },
            target: '#participant-profile-header',
            title: 'Creator Profile',
        },
        {
            content: 'Review content submitted by the creator. You can watch the video, download the file, approve it, or request a revision.',
            disableBeacon: true,
            disableOverlayClose: true,
            hideCloseButton: false,
            hideFooter: false,
            placement: 'bottom',
            spotlightClicks: false,
            styles: { options: { zIndex: 9998 } },
            target: '#participant-submitted-work',
            title: 'Submitted Work',
        },
        {
            content: 'Track the status of each deliverable — how many are complete, pending, or still awaiting submission.',
            disableBeacon: true,
            disableOverlayClose: true,
            hideCloseButton: false,
            hideFooter: false,
            placement: 'right',
            spotlightClicks: false,
            styles: { options: { zIndex: 9998 } },
            target: '#participant-deliverables',
            title: 'Deliverables',
        },
        {
            content: 'Communicate directly with the creator — share feedback, brief updates, or ask questions here.',
            disableBeacon: true,
            disableOverlayClose: true,
            hideCloseButton: false,
            hideFooter: false,
            placement: 'left',
            spotlightClicks: false,
            styles: { options: { zIndex: 9998 } },
            target: '#participant-discussion',
            title: 'Discussion',
        },
        {
            content: 'After the campaign wraps, rate your experience working with this creator to help build their reputation on Thspian.',
            disableBeacon: true,
            disableOverlayClose: true,
            hideCloseButton: false,
            hideFooter: false,
            placement: 'bottom',
            spotlightClicks: false,
            styles: { options: { zIndex: 9998 } },
            target: '#influencer-review-prompt',
            title: 'Rate the Creator',
        },
    ],
};

export const campaignDetailTutorialSteps: ITutorialState = {
    run: true,
    steps: [
        {
            content: 'Your campaign stats at a glance — budget spent, timeline, number of creators, and reward type.',
            disableBeacon: true,
            disableOverlayClose: true,
            hideCloseButton: false,
            hideFooter: false,
            placement: 'bottom',
            spotlightClicks: false,
            styles: { options: { zIndex: 9998 } },
            target: '#campaign-overview-cards',
            title: 'Campaign Overview',
        },
        {
            content: 'Toggle to publish your campaign so creators can discover and apply. Campaign must be funded first.',
            disableBeacon: true,
            disableOverlayClose: true,
            hideCloseButton: false,
            hideFooter: false,
            placement: 'bottom',
            spotlightClicks: false,
            styles: { options: { zIndex: 9998 } },
            target: '#campaign-publish-toggle',
            title: 'Publish Your Campaign',
        },
        {
            content: 'Copy a shareable public link, view campaign analytics, or edit campaign details from here.',
            disableBeacon: true,
            disableOverlayClose: true,
            hideCloseButton: false,
            hideFooter: false,
            placement: 'bottom',
            spotlightClicks: false,
            styles: { options: { zIndex: 9998 } },
            target: '#campaign-detail-actions',
            title: 'Campaign Actions',
        },
        {
            content: 'Review new applications, manage accepted creators, and track pending invites all in one place.',
            disableBeacon: true,
            disableOverlayClose: true,
            hideCloseButton: false,
            hideFooter: false,
            placement: 'top',
            spotlightClicks: false,
            styles: { options: { zIndex: 9998 } },
            target: '#campaign-participants',
            title: 'Creators & Applications',
        },
    ],
};

export const campaignCreationTutorialSteps: ITutorialState = {
    run: true,
    steps: [
        {
            content: 'Choose between a UGC Campaign (creator partnership), Contest (open competition), or Audition (cast & crew). Each type has its own workflow.',
            disableBeacon: true,
            disableOverlayClose: true,
            hideCloseButton: false,
            hideFooter: false,
            placement: 'bottom',
            spotlightClicks: false,
            styles: { options: { zIndex: 9998 } },
            target: '#campaign-type-selector',
            title: 'Campaign Type',
        },
        {
            content: 'Your campaign is built across 3 steps — Campaign Identity, Budget & Targeting, and Details & Launch. Complete each step before moving on.',
            disableBeacon: true,
            disableOverlayClose: true,
            hideCloseButton: false,
            hideFooter: false,
            placement: 'bottom',
            spotlightClicks: false,
            styles: { options: { zIndex: 9998 } },
            target: '#campaign-stepper',
            title: 'Step-by-Step Flow',
        },
        {
            content: 'As you fill in your campaign details, matching creators are automatically suggested here. You can invite them directly before you even launch.',
            disableBeacon: true,
            disableOverlayClose: true,
            hideCloseButton: false,
            hideFooter: false,
            placement: 'left',
            spotlightClicks: false,
            styles: { options: { zIndex: 9998 } },
            target: '#curated-creators-panel',
            title: 'Curated Creators',
        },
        {
            content: 'Save your progress as a draft at any time, or continue to the next step when you\'re ready. Your draft is auto-saved as you go.',
            disableBeacon: true,
            disableOverlayClose: true,
            hideCloseButton: false,
            hideFooter: false,
            placement: 'top',
            spotlightClicks: false,
            styles: { options: { zIndex: 9998 } },
            target: '#campaign-step-footer',
            title: 'Save Draft or Continue',
        },
    ],
};

export const brandTutorialSteps: ITutorialState = {
    run: true,
    steps: [
        {
            content: 'Navigate between your Dashboard, Campaigns, Talent search, and Wallet from here.',
            disableBeacon: true,
            disableOverlayClose: true,
            hideCloseButton: false,
            hideFooter: false,
            placement: 'bottom',
            spotlightClicks: false,
            styles: { options: { zIndex: 9998 } },
            target: '#brand-nav',
            title: 'Navigation',
        },
        {
            content: 'Create a new UGC campaign and start hiring creators to promote your brand.',
            disableBeacon: true,
            disableOverlayClose: true,
            hideCloseButton: false,
            hideFooter: false,
            placement: 'bottom',
            spotlightClicks: false,
            styles: { options: { zIndex: 9998 } },
            target: '#brand-create-campaign-btn',
            title: 'Create a Campaign',
        },
        {
            content: 'Browse and discover creators that match your brand. Filter by niche, reach, and engagement.',
            disableBeacon: true,
            disableOverlayClose: true,
            hideCloseButton: false,
            hideFooter: false,
            placement: 'bottom',
            spotlightClicks: false,
            styles: { options: { zIndex: 9998 } },
            target: '#brand-find-talent-btn',
            title: 'Find Talent',
        },
        {
            content: 'Track your total active spend, live campaigns, and content pending your review at a glance.',
            disableBeacon: true,
            disableOverlayClose: true,
            hideCloseButton: false,
            hideFooter: false,
            placement: 'bottom',
            spotlightClicks: false,
            styles: { options: { zIndex: 9998 } },
            target: '#brand-metrics',
            title: 'Your Key Metrics',
        },
        {
            content: 'See all your running campaigns here. Click into any campaign to review submissions and manage creators.',
            disableBeacon: true,
            disableOverlayClose: true,
            hideCloseButton: false,
            hideFooter: false,
            placement: 'top',
            spotlightClicks: false,
            styles: { options: { zIndex: 9998 } },
            target: '#brand-active-campaigns',
            title: 'Active Campaigns',
        },
        {
            content: 'Switch back to your creator account to browse campaigns and submit content.',
            disableBeacon: true,
            disableOverlayClose: true,
            hideCloseButton: false,
            hideFooter: false,
            placement: 'bottom',
            spotlightClicks: false,
            styles: { options: { zIndex: 9998 } },
            target: '#switch-view-btn',
            title: 'Switch Mode',
        },
    ],
};

export const projectGuideSteps: ITutorialState = {
    run: true,
    steps: [
        {
            content: 'Click the icon to download or edit project',
            disableBeacon: false,
            disableOverlayClose: false,
            hideCloseButton: false,
            hideFooter: false,
            placement: 'right',
            spotlightClicks: true,
            hideBackButton: false,
            styles: {
                options: {
                    zIndex: 2,
                },
            },
            target: '#download-video',
            title: 'Download processed project here',
            showProgress: false,
            showSkipButton: true,
        },
    ],
};

export const videoGuideSteps: ITutorialState = {
    run: true,
    steps: [
        {
            content: 'Click here to watch more videos',
            disableBeacon: true,
            disableOverlayClose: false,
            hideCloseButton: false,
            hideFooter: false,
            placement: 'right',
            spotlightClicks: true,
            hideBackButton: true,
            styles: {
                options: {
                    zIndex: 9999,
                },
            },
            target: '#more-videos',
            title: 'Watch More',
            showProgress: false,
            showSkipButton: true,
            isFixed: false,
        },
    ],
};

export const tutorialSteps: ITutorialState = {
    run: true,
    steps: [
        {
            content: 'Use the menu to navigate Thspian',
            disableBeacon: true,
            disableOverlayClose: true,
            hideCloseButton: false,
            hideFooter: false,
            placement: 'right',
            spotlightClicks: true,
            styles: {
                options: {
                    zIndex: 9998,
                },
            },
            target: '#menu-icon',
            title: 'Navigation',
        },
        {
            content: 'Search for other thspians, projects and contests.',
            disableBeacon: true,
            disableOverlayClose: true,
            hideCloseButton: false,
            hideFooter: false,
            placement: 'left',
            spotlightClicks: false,
            styles: {
                options: {
                    zIndex: 9998,
                },
            },
            target: '#search-bar',
            title: 'Search',
        },
        {
            content:
                'Switch between your creator and host/brand accounts. As a host or brand, you can create campaigns, post auditions, and hire creators.',
            disableBeacon: true,
            disableOverlayClose: true,
            hideCloseButton: false,
            hideFooter: false,
            placement: 'bottom',
            spotlightClicks: false,
            styles: {
                options: {
                    zIndex: 9998,
                },
            },
            target: '#switch-view-btn',
            title: 'Switch Mode',
        },
        // {
        //     content: 'Toggle between light and dark mode.',
        //     disableBeacon: true,
        //     disableOverlayClose: true,
        //     hideCloseButton: false,
        //     hideFooter: false,
        //     placement: 'left',
        //     spotlightClicks: false,
        //     styles: {
        //         options: {
        //             zIndex: 9998,
        //         },
        //     },
        //     target: '#toggle-icon',
        //     title: 'Toggle Dark Mode',
        // },
        {
            content:
                'Create projects to showcase your talents. You can enter your projects in contests and win prizes!',
            disableBeacon: true,
            disableOverlayClose: true,
            hideCloseButton: false,
            hideFooter: false,
            placement: 'bottom',
            spotlightClicks: true,
            styles: {
                options: {
                    zIndex: 9998,
                },
            },
            target: '#create-project-icon',
            title: 'Create A Project',
        },
        {
            content: 'View your created projects here.',
            disableBeacon: true,
            disableOverlayClose: true,
            hideCloseButton: false,
            hideFooter: false,
            placement: 'right',
            spotlightClicks: false,
            styles: {
                options: {
                    zIndex: 9998,
                    width: '100%',
                },
            },
            target: '#projects-menu-icon',
            title: 'Projects',
        },
        {
            content: 'View your profile, change your name / username here.',
            disableBeacon: true,
            disableOverlayClose: true,
            hideCloseButton: false,
            hideFooter: false,
            placement: 'right',
            spotlightClicks: false,
            styles: {
                options: {
                    zIndex: 9998,
                    width: '100%',
                },
            },
            target: '#profile-menu-icon',
            title: 'Profile',
        },
        {
            content: 'Manage your account and creations from here',
            disableBeacon: true,
            disableOverlayClose: true,
            hideCloseButton: false,
            hideFooter: false,
            placement: 'right',
            spotlightClicks: false,
            styles: {
                options: {
                    zIndex: 9998,
                    width: '100%',
                },
            },
            target: '#dashboard-menu-icon',
            title: 'Dashboard',
        },
        {
            content: 'Tap here to create a new campaign or a new job!',
            disableBeacon: true,
            disableOverlayClose: true,
            hideCloseButton: false,
            hideFooter: false,
            placement: 'auto',
            spotlightClicks: false,
            styles: {
                options: {
                    zIndex: 9998,
                    width: '100%',
                },
            },
            target: '#campaign-audition',
            title: 'Campaigns & Auditions',
        },

        {
            content: 'View updates on your project, contests and more!',
            disableBeacon: true,
            disableOverlayClose: true,
            hideCloseButton: false,
            hideFooter: false,
            placement: 'auto',
            spotlightClicks: false,
            styles: {
                options: {
                    zIndex: 9998,
                    width: '100%',
                },
            },
            target: '#notificationIcon',
            title: 'Notifications',
        },
        {
            content: 'Contact support for any assistance or chat with other thspians!',
            disableBeacon: true,
            disableOverlayClose: true,
            hideCloseButton: false,
            hideFooter: false,
            placement: 'auto',
            spotlightClicks: false,
            styles: {
                options: {
                    zIndex: 9998,
                    width: '100%',
                },
            },
            target: '#messageIcon',
            title: 'Messages',
        },
        // {
        //     content: 'Toggle here to filter between jobs, and campaigns and events.',
        //     disableBeacon: true,
        //     disableOverlayClose: false,
        //     hideCloseButton: false,
        //     hideFooter: false,
        //     placement: 'right',
        //     spotlightClicks: true,
        //     hideBackButton: false,
        //     styles: {
        //         options: {
        //             zIndex: -1,
        //         },
        //     },
        //     target: '#feed-filter',
        //     title: 'Filter Feed',
        //     showProgress: false,
        //     showSkipButton: true,
        // },
    ],
};
